import React, { useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Switch,
  Stack,
  Chip,
  Alert,
  LinearProgress,
  Tooltip,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
} from '@mui/material';
import {
  Today as TodayIcon,
  DateRange as DateRangeIcon,
  CalendarMonth as CalendarMonthIcon,
  History as HistoryIcon,
  CloudDownload as CloudDownloadIcon,
  CurrencyExchange as CurrencyExchangeIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
} from '@mui/icons-material';
import { useQuery, useMutation, useApolloClient } from '@apollo/client';
import {
  GET_ALL_GLOBAL_CURRENCIES,
  GET_LATEST_GLOBAL_RATES,
  GET_GLOBAL_CURRENCY_STATS,
  GET_GLOBAL_RATES_FOR_DATE,
} from '../graphql/queries';
import {
  TOGGLE_GLOBAL_CURRENCY,
  SYNC_GLOBAL_RATES_TODAY,
  SYNC_GLOBAL_RATES_LAST_MONTH,
  SYNC_GLOBAL_RATES_LAST_90_DAYS,
  SYNC_GLOBAL_RATES_HISTORICAL,
  SYNC_GLOBAL_RATES_ALL,
} from '../graphql/mutations';

const GlobalCurrenciesTab = () => {
  const apolloClient = useApolloClient();
  const [syncLoading, setSyncLoading] = useState(false);
  const [syncResult, setSyncResult] = useState(null);
  const [historicalDialog, setHistoricalDialog] = useState(false);
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [lookupDate, setLookupDate] = useState('');
  const [lookupRates, setLookupRates] = useState(null);
  const [lookupLoading, setLookupLoading] = useState(false);

  // Queries
  const { data: currenciesData, loading: currenciesLoading, refetch: refetchCurrencies } = useQuery(GET_ALL_GLOBAL_CURRENCIES);
  const { data: latestRatesData, refetch: refetchRates } = useQuery(GET_LATEST_GLOBAL_RATES);
  const { data: statsData, refetch: refetchStats } = useQuery(GET_GLOBAL_CURRENCY_STATS);

  // Mutations
  const [toggleCurrency] = useMutation(TOGGLE_GLOBAL_CURRENCY);
  const [syncToday] = useMutation(SYNC_GLOBAL_RATES_TODAY);
  const [syncLastMonth] = useMutation(SYNC_GLOBAL_RATES_LAST_MONTH);
  const [syncLast90Days] = useMutation(SYNC_GLOBAL_RATES_LAST_90_DAYS);
  const [syncHistorical] = useMutation(SYNC_GLOBAL_RATES_HISTORICAL);
  const [syncAll] = useMutation(SYNC_GLOBAL_RATES_ALL);

  const handleToggleCurrency = async (code) => {
    try {
      await toggleCurrency({ variables: { code } });
      refetchCurrencies();
    } catch (error) {
      console.error('Error toggling currency:', error);
    }
  };

  const handleSync = async (syncFn, type) => {
    setSyncLoading(true);
    setSyncResult(null);
    try {
      const { data } = await syncFn();
      const result = data[Object.keys(data)[0]];
      setSyncResult(result);
      refetchCurrencies();
      refetchRates();
      refetchStats();
    } catch (error) {
      setSyncResult({ success: false, message: error.message });
    } finally {
      setSyncLoading(false);
    }
  };

  const handleSyncHistorical = async () => {
    if (!fromDate || !toDate) return;
    setHistoricalDialog(false);
    setSyncLoading(true);
    setSyncResult(null);
    try {
      const { data } = await syncHistorical({ variables: { fromDate, toDate } });
      setSyncResult(data.syncGlobalRatesHistorical);
      refetchCurrencies();
      refetchRates();
      refetchStats();
    } catch (error) {
      setSyncResult({ success: false, message: error.message });
    } finally {
      setSyncLoading(false);
    }
  };

  const handleLookupRates = async () => {
    if (!lookupDate) return;
    setLookupLoading(true);
    try {
      const { data } = await apolloClient.query({
        query: GET_GLOBAL_RATES_FOR_DATE,
        variables: { date: lookupDate },
        fetchPolicy: 'network-only',
      });
      setLookupRates(data.globalRatesForDate);
    } catch (error) {
      console.error('Error fetching rates:', error);
    } finally {
      setLookupLoading(false);
    }
  };

  const currencies = currenciesData?.allGlobalCurrencies || [];
  const latestRates = latestRatesData?.latestGlobalRates || [];
  const stats = statsData?.globalCurrencyStats;

  // Create a map of currency code to latest rate
  const ratesMap = {};
  latestRates.forEach(rate => {
    ratesMap[rate.currencyCode] = rate;
  });

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        <CurrencyExchangeIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
        Глобални валути (ECB)
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Централизирано управление на валути и курсове от Европейската централна банка.
        Активираните валути са достъпни за всички компании в системата.
      </Typography>

      {/* Stats Cards */}
      {stats && (
        <Stack direction="row" spacing={2} sx={{ mb: 3 }}>
          <Card sx={{ minWidth: 150 }}>
            <CardContent>
              <Typography color="text.secondary" variant="body2">Общо валути</Typography>
              <Typography variant="h4">{stats.totalCurrencies}</Typography>
            </CardContent>
          </Card>
          <Card sx={{ minWidth: 150 }}>
            <CardContent>
              <Typography color="text.secondary" variant="body2">Активирани</Typography>
              <Typography variant="h4" color="success.main">{stats.enabledCurrencies}</Typography>
            </CardContent>
          </Card>
          <Card sx={{ minWidth: 150 }}>
            <CardContent>
              <Typography color="text.secondary" variant="body2">Записани курсове</Typography>
              <Typography variant="h4">{stats.totalRates}</Typography>
            </CardContent>
          </Card>
          <Card sx={{ minWidth: 200 }}>
            <CardContent>
              <Typography color="text.secondary" variant="body2">Последни курсове</Typography>
              <Typography variant="h6">{stats.latestRateDate || 'Няма данни'}</Typography>
            </CardContent>
          </Card>
        </Stack>
      )}

      {/* Sync Buttons */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="subtitle1" gutterBottom>Синхронизиране на курсове от ECB</Typography>
          <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
            <Button
              variant="contained"
              startIcon={<TodayIcon />}
              onClick={() => handleSync(syncToday, 'today')}
              disabled={syncLoading}
            >
              Днес
            </Button>
            <Button
              variant="outlined"
              startIcon={<CalendarMonthIcon />}
              onClick={() => handleSync(syncLastMonth, 'month')}
              disabled={syncLoading}
            >
              Последен месец
            </Button>
            <Button
              variant="outlined"
              startIcon={<DateRangeIcon />}
              onClick={() => handleSync(syncLast90Days, '90days')}
              disabled={syncLoading}
            >
              Последни 90 дни
            </Button>
            <Button
              variant="outlined"
              startIcon={<HistoryIcon />}
              onClick={() => setHistoricalDialog(true)}
              disabled={syncLoading}
            >
              Период...
            </Button>
            <Button
              variant="outlined"
              color="warning"
              startIcon={<CloudDownloadIcon />}
              onClick={() => handleSync(syncAll, 'all')}
              disabled={syncLoading}
            >
              Всички (бавно)
            </Button>
          </Stack>

          {syncLoading && <LinearProgress sx={{ mt: 2 }} />}

          {syncResult && (
            <Alert
              severity={syncResult.success ? 'success' : 'error'}
              sx={{ mt: 2 }}
              onClose={() => setSyncResult(null)}
            >
              {syncResult.message}
              {syncResult.success && (
                <Typography variant="body2">
                  Валути: {syncResult.currenciesCount}, Курсове: {syncResult.ratesCount}
                </Typography>
              )}
            </Alert>
          )}
        </CardContent>
      </Card>

      {/* Lookup Rates */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="subtitle1" gutterBottom>Преглед на курсове за дата</Typography>
          <Stack direction="row" spacing={2} alignItems="center">
            <TextField
              type="date"
              size="small"
              value={lookupDate}
              onChange={(e) => setLookupDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              label="Дата"
            />
            <Button
              variant="outlined"
              onClick={handleLookupRates}
              disabled={!lookupDate || lookupLoading}
            >
              Покажи курсове
            </Button>
          </Stack>

          {lookupLoading && <LinearProgress sx={{ mt: 2 }} />}

          {lookupRates && lookupRates.length > 0 && (
            <TableContainer component={Paper} sx={{ mt: 2, maxHeight: 300 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>Валута</TableCell>
                    <TableCell align="right">Курс (1 EUR =)</TableCell>
                    <TableCell>Дата</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {lookupRates.map((rate) => (
                    <TableRow key={rate.id}>
                      <TableCell>{rate.currencyCode}</TableCell>
                      <TableCell align="right">{parseFloat(rate.rate).toFixed(4)}</TableCell>
                      <TableCell>{rate.rateDate}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {lookupRates && lookupRates.length === 0 && (
            <Alert severity="info" sx={{ mt: 2 }}>
              Няма курсове за избраната дата
            </Alert>
          )}
        </CardContent>
      </Card>

      <Divider sx={{ my: 3 }} />

      {/* Currencies Table */}
      <Typography variant="subtitle1" gutterBottom>Списък на валутите</Typography>

      {currenciesLoading ? (
        <LinearProgress />
      ) : (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Код</TableCell>
                <TableCell>Име (EN)</TableCell>
                <TableCell>Име (BG)</TableCell>
                <TableCell>Символ</TableCell>
                <TableCell align="center">ECB</TableCell>
                <TableCell align="center">Активна</TableCell>
                <TableCell align="right">Последен курс</TableCell>
                <TableCell align="center">Последна синх.</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {currencies.map((currency) => {
                const rate = ratesMap[currency.code];
                return (
                  <TableRow
                    key={currency.code}
                    sx={{
                      backgroundColor: currency.enabledGlobally ? 'action.selected' : 'inherit',
                      '&:hover': { backgroundColor: 'action.hover' }
                    }}
                  >
                    <TableCell>
                      <Typography variant="body2" fontWeight="bold">
                        {currency.code}
                      </Typography>
                    </TableCell>
                    <TableCell>{currency.nameEn}</TableCell>
                    <TableCell>{currency.nameBg}</TableCell>
                    <TableCell>
                      <Chip label={currency.symbol || '-'} size="small" variant="outlined" />
                    </TableCell>
                    <TableCell align="center">
                      {currency.activeInEcb ? (
                        <CheckCircleIcon color="success" fontSize="small" />
                      ) : (
                        <CancelIcon color="disabled" fontSize="small" />
                      )}
                    </TableCell>
                    <TableCell align="center">
                      <Switch
                        checked={currency.enabledGlobally}
                        onChange={() => handleToggleCurrency(currency.code)}
                        size="small"
                        disabled={currency.code === 'EUR'}
                      />
                    </TableCell>
                    <TableCell align="right">
                      {rate ? (
                        <Tooltip title={`1 EUR = ${rate.rate} ${currency.code}`}>
                          <span>{parseFloat(rate.rate).toFixed(4)}</span>
                        </Tooltip>
                      ) : '-'}
                    </TableCell>
                    <TableCell align="center">
                      <Typography variant="caption" color="text.secondary">
                        {currency.lastSyncAt ? new Date(currency.lastSyncAt).toLocaleDateString('bg-BG') : '-'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Historical Sync Dialog */}
      <Dialog open={historicalDialog} onClose={() => setHistoricalDialog(false)}>
        <DialogTitle>Синхронизиране на исторически курсове</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1, minWidth: 300 }}>
            <TextField
              type="date"
              label="От дата"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
            <TextField
              type="date"
              label="До дата"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
            <Alert severity="info">
              Синхронизирането на голям период може да отнеме време.
              За периоди над 90 дни се използва пълният ECB архив.
            </Alert>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setHistoricalDialog(false)}>Отказ</Button>
          <Button
            onClick={handleSyncHistorical}
            variant="contained"
            disabled={!fromDate || !toDate}
          >
            Синхронизирай
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default GlobalCurrenciesTab;
